import {
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { PatientDetailScreenQuery } from "~/graphql/__generated__/graphql";
import VisitStatusChip from "../command/VisitStatusChip";
import { differenceInMinutes } from "date-fns";
import { formatProvidersNames } from "~/utils/visitsHelper";
import VisitPopup from "~/components/command/VisitPopup";
import { CalendarPopup } from "~/components/common/calendar/CalendarPopup";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";

interface VisitCardProps {
  visit: PatientDetailScreenQuery["visit"][number];
  onCancel: (visitId: number) => void;
  onEdit: (visitId: number) => void;
  isDetailsOpen: boolean;
  detailsAnchor: HTMLElement | null;
  onOpenDetails: (anchorEl: HTMLElement) => void;
  onCloseDetails: () => void;
}

const VisitCard: React.FC<VisitCardProps> = ({
  visit,
  onCancel,
  onEdit,
  isDetailsOpen,
  detailsAnchor,
  onOpenDetails,
  onCloseDetails,
}) => {
  const { datef, timeLabelWithZone } = useZonedDateFormat();
  const theme = useTheme();

  const providerFullName = formatProvidersNames(visit.visitResources);
  const date = `${datef(
    visit.startTime,
    "MMM do yyyy"
  )}`;

  const visitFormattedTime = `Arrival window: ${timeLabelWithZone(
    visit.arrivalWindowStartTime,
    false,
    visit.addressTimezone
  )}
  -
  ${timeLabelWithZone(
    visit.arrivalWindowEndTime,
    true,
    visit.addressTimezone
  )}`;

  return (
    <Card
      variant="outlined"
      sx={{ marginBottom: 2, padding: 0, ":hover": { borderColor: theme.palette.grey[500] } }}
    >
      <CardHeader
        onClick={(e) => onOpenDetails(e.currentTarget)}
        sx={{ cursor: "pointer" }}
        title={
          <Stack direction='row' justifyContent='space-between'>
            <Typography variant="subtitle1">
              <b>{date}</b> {visitFormattedTime}
            </Typography>
            <VisitStatusChip
              cancellationReason={visit.cancellationReason?.description}
              status={visit.eventStatus}
              sx={{ order: 2 }}
            />
          </Stack>
        }
      />
      <CardContent
        onClick={(e) => onOpenDetails(e.currentTarget)}
        sx={{ cursor: "pointer", paddingTop: 0 }}
      >
        <Grid container direction='column'>

          <Stack direction='row' spacing={3} width={'100%'}>
            <Grid marginBottom={1}>
              <Typography variant="button">Visit Type</Typography>
              <Typography variant="body1" fontWeight={500}>
                {visit.visitType?.name}
              </Typography>
            </Grid>
            <Grid>
              <Typography variant="button">Provider(s)</Typography>
              <Typography variant="body1">
                {providerFullName}
              </Typography>
            </Grid>
            <Grid>
              <Typography variant="button">Visit Duration</Typography>
              <Typography variant="body1" gutterBottom>
                {differenceInMinutes(visit.endTime, visit.startTime)} min
              </Typography>
            </Grid>
          </Stack>
        </Grid>
      </CardContent>

      {isDetailsOpen && detailsAnchor && (
        <CalendarPopup
          isOpen={true}
          visitAnchor={detailsAnchor}
          onClose={onCloseDetails}
        >
          <VisitPopup
            visit={visit}
            onClose={onCloseDetails}
            onCancel={onCancel}
            onEdit={onEdit}
          />
        </CalendarPopup>
      )}
    </Card>
  );
};

export default VisitCard;
